import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from "axios";
import { OpenAPI } from "../api/core/OpenAPI";
import { getCsrfToken } from "../utils/csrf";

// ウォーターマークタイプの定数
export const WATERMARK_TYPE = {
  NONE: 'none',
  INDIVIDUAL_INPUT: 'individual_input',
  CORPORATE_LOGO: 'corporate_logo'
} as const;

export type WatermarkType = typeof WATERMARK_TYPE[keyof typeof WATERMARK_TYPE];

// ウォーターマーク配置位置の定数
export const WATERMARK_POSITION = [
  { value: 'top-left', label: '左上' },
  { value: 'top-center', label: '中上' },
  { value: 'top-right', label: '右上' },
  { value: 'middle-left', label: '左中' },
  { value: 'center', label: '中央' },
  { value: 'middle-right', label: '右中' },
  { value: 'bottom-left', label: '左下' },
  { value: 'bottom-center', label: '中下' },
  { value: 'bottom-right', label: '右下' },
] as const;

export type WatermarkPositionValue = typeof WATERMARK_POSITION[number]['value'];

const initialState = {
  watermark_type: undefined as WatermarkType | undefined,
  watermark_position: undefined as WatermarkPositionValue | undefined,
  individual_input_transparency: undefined as number | undefined,
  individual_input_scale: undefined as number | undefined,
  individual_input_text: undefined as string | null | undefined,
  individual_input_color: undefined as string | undefined,
  individual_input_is_visible: undefined as boolean | undefined,
  corporate_logo_transparency: undefined as number | undefined,
  corporate_logo_scale: undefined as number | undefined,
  corporate_logo_file_name: undefined as string | null | undefined,
  corporate_logo_file_data: undefined as string | null | undefined,
  corporate_logo_is_visible: undefined as boolean | undefined,
  annotation_text: undefined as string | null | undefined,
  annotation_transparency: undefined as number | undefined,
  annotation_scale: undefined as number | undefined,
  annotation_color: undefined as string | undefined,
  annotation_position: undefined as string | undefined,
  annotation_is_visible: undefined as boolean | undefined,
  default_room_type: undefined as string | undefined,
  default_life_style: undefined as string | undefined,
  isLoading: false,
  error: null as string | null,
  success_message: null as string | null,
  // 設定が保存されたことを示すフラグ（タイムスタンプで管理）
  lastSavedTimestamp: null as number | null,
};

// 非同期アクション
const fetchServiceSettings = createAsyncThunk(
  'fetchServiceSettings',
  async (_, thunkAPI: any) => {
    try {
      thunkAPI.dispatch(serviceSettingsSlice.actions.setLoading(true));
      const response = await axios.get('/api/service-settings', {
        withCredentials: true,
      });

      if (response.data.success) {
        thunkAPI.dispatch(serviceSettingsSlice.actions.setSettings(response.data.data));
        return;
      }

      thunkAPI.dispatch(serviceSettingsSlice.actions.setError(
        response.data.message || "設定の取得に失敗しました"
      ));
    } catch (error: any) {
      thunkAPI.dispatch(serviceSettingsSlice.actions.setError(
        error.response?.data?.message || "ネットワークエラーが発生しました"
      ));
    } finally {
      thunkAPI.dispatch(serviceSettingsSlice.actions.setLoading(false));
    }
  }
);

const saveServiceSettings = createAsyncThunk(
  'saveServiceSettings',
  async (_, thunkAPI: any) => {
    try {
      thunkAPI.dispatch(serviceSettingsSlice.actions.setLoading(true));
      const settings = thunkAPI.getState().serviceSettings;
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        '/api/service-settings',
        settings,
        {
          headers: {
            "X-CSRFToken": csrfToken,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data.success) {
        thunkAPI.dispatch(serviceSettingsSlice.actions.setSettings(response.data.data));
        thunkAPI.dispatch(serviceSettingsSlice.actions.setSuccessMessage(response.data.message));
        // 保存成功時にタイムスタンプを更新
        thunkAPI.dispatch(serviceSettingsSlice.actions.setLastSavedTimestamp(Date.now()));
        return;
      }

      thunkAPI.dispatch(serviceSettingsSlice.actions.setError(response.data.message));
    } catch (error: any) {
      thunkAPI.dispatch(serviceSettingsSlice.actions.setError(
        error.response?.data?.message || "ネットワークエラーが発生しました"
      ));
    } finally {
      thunkAPI.dispatch(serviceSettingsSlice.actions.setLoading(false));
    }
  }
);

const serviceSettingsSlice = createSlice({
  name: 'serviceSettings',
  initialState,
  reducers: {
    // サービス設定の取得開始
    fetchServiceSettingsRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    // サービス設定の取得成功
    fetchServiceSettingsSuccess: (state, action) => {
      state.isLoading = false;
      state.error = null;
      // 取得したデータで更新
      Object.assign(state, action.payload);
    },
    // サービス設定の取得失敗
    fetchServiceSettingsFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    // サービス設定の更新開始
    updateServiceSettingsRequest: (state) => {
      state.isLoading = true;
      state.error = null;
      state.success_message = null;
    },
    // サービス設定の更新成功
    updateServiceSettingsSuccess: (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.success_message = action.payload.message || '設定を保存しました';
      // 更新したデータで上書き
      if (action.payload.data) {
        Object.assign(state, action.payload.data);
      }
    },
    // サービス設定の更新失敗
    updateServiceSettingsFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.success_message = null;
    },
    // エラーメッセージをクリア
    clearError: (state) => {
      state.error = null;
    },
    // 成功メッセージをクリア
    clearSuccessMessage: (state) => {
      state.success_message = null;
    },
    // 設定をリセット
    resetSettings: (state) => {
      Object.assign(state, initialState);
    },
    // ローカルで設定を更新（API呼び出しなし）
    updateLocalSettings: (state, action) => {
      Object.assign(state, action.payload);
    },
    // 個別の設定更新
    setWatermarkType: (state, action) => {
      state.watermark_type = action.payload;
    },
    setWatermarkPosition: (state, action) => {
      state.watermark_position = action.payload;
    },
    setIndividualInputTransparency: (state, action) => {
      state.individual_input_transparency = action.payload;
    },
    setIndividualInputScale: (state, action) => {
      state.individual_input_scale = action.payload;
    },
    setIndividualInputText: (state, action) => {
      state.individual_input_text = action.payload;
    },
    setIndividualInputColor: (state, action) => {
      state.individual_input_color = action.payload;
    },
    setIndividualInputIsVisible: (state, action) => {
      state.individual_input_is_visible = action.payload;
    },
    setCorporateLogoTransparency: (state, action) => {
      state.corporate_logo_transparency = action.payload;
    },
    setCorporateLogoScale: (state, action) => {
      state.corporate_logo_scale = action.payload;
    },
    setCorporateLogoFileName: (state, action) => {
      state.corporate_logo_file_name = action.payload;
    },
    setCorporateLogoFileData: (state, action) => {
      state.corporate_logo_file_data = action.payload;
    },
    setCorporateLogoFile: (state, action) => {
      state.corporate_logo_file_name = action.payload.name;
      state.corporate_logo_file_data = action.payload.data;
      // 企業ロゴが設定されたら、デフォルトで表示をONにする
      if (action.payload.data && state.corporate_logo_is_visible === undefined) {
        state.corporate_logo_is_visible = true;
      }
    },
    setCorporateLogoIsVisible: (state, action) => {
      state.corporate_logo_is_visible = action.payload;
    },
    setAnnotationText: (state, action) => {
      state.annotation_text = action.payload;
    },
    setAnnotationTransparency: (state, action) => {
      state.annotation_transparency = action.payload;
    },
    setAnnotationScale: (state, action) => {
      state.annotation_scale = action.payload;
    },
    setAnnotationColor: (state, action) => {
      state.annotation_color = action.payload;
    },
    setAnnotationPosition: (state, action) => {
      state.annotation_position = action.payload;
    },
    setAnnotationIsVisible: (state, action) => {
      state.annotation_is_visible = action.payload;
    },
    setDefaultRoomType: (state, action) => {
      state.default_room_type = action.payload;
    },
    setDefaultLifeStyle: (state, action) => {
      state.default_life_style = action.payload;
    },
    setSettings: (state, action) => {
      return { ...state, ...action.payload, error: null, success_message: null };
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.success_message = null;
    },
    setSuccessMessage: (state, action) => {
      state.success_message = action.payload;
      state.error = null;
    },
    clearMessages: (state) => {
      state.error = null;
      state.success_message = null;
    },
    // 保存タイムスタンプを設定
    setLastSavedTimestamp: (state, action) => {
      state.lastSavedTimestamp = action.payload;
    },
  },
});

const serviceSettingsActions = {
  ...serviceSettingsSlice.actions,
  fetchServiceSettings,
  saveServiceSettings,
};

export { serviceSettingsActions };
export default serviceSettingsSlice.reducer;